import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { MapPin, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import TripQuizDialog from "@/components/TripQuiz/TripQuizDialog";
import { cn } from "@/lib/utils";

const FloatingTripQuiz = () => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const [quizOpen, setQuizOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [isClosed, setIsClosed] = useState(false);
  const isGreek = i18n.language === "el";

  const [isVisible, setIsVisible] = useState(false);

  // Hide completely in admin and partner areas
  if (location.pathname.startsWith("/admin") || location.pathname.startsWith("/partner")) {
    return null;
  }

  // Show only after scrolling past the hero section (e.g. 450px)
  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(window.scrollY > 450);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsClosed(true);
  };

  if (isClosed || !isVisible) {
    return <TripQuizDialog open={quizOpen} onOpenChange={setQuizOpen} />;
  }

  return (
    <>
      <div className="fixed bottom-[92px] md:bottom-[104px] right-5 md:right-8 z-[50] flex items-center gap-3">
        <div
          className={cn(
            "bg-white text-ocean-deep border border-ocean-deep/10 px-4 py-2 rounded-2xl text-xs font-semibold font-body shadow-[0_10px_30px_-10px_rgba(15,40,70,0.25)] transition-all duration-300 pointer-events-none whitespace-nowrap",
            showTooltip
              ? "opacity-100 translate-x-0 scale-100"
              : "opacity-0 translate-x-4 scale-95 pointer-events-none"
          )}
        >
          <div className="relative">
            {isGreek ? "Δωρεάν πλάνο διακοπών 🧭" : "Free custom trip plan 🧭"}
            <div className="absolute top-1/2 -right-5 -translate-y-1/2 border-8 border-transparent border-l-white drop-shadow-[1px_0_0_rgba(15,40,70,0.05)]" />
          </div>
        </div>

        <div className="relative">
          <button
            onClick={() => setQuizOpen(true)}
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
            aria-label="Trip Planner Quiz"
            className="group relative flex h-14 w-14 items-center justify-center rounded-full bg-ocean-deep text-white shadow-[0_8px_25px_rgba(15,40,70,0.4)] transition-all duration-300 hover:scale-110 hover:-translate-y-1 hover:shadow-[0_12px_30px_rgba(15,40,70,0.6)]"
          >
            <MapPin className="relative z-10 h-6 w-6 transition-transform duration-300 group-hover:scale-110" />
          </button>
          
          <button
            onClick={handleClose}
            className="absolute -top-1 -right-1 z-20 flex h-5 w-5 items-center justify-center rounded-full bg-white text-ocean-deep shadow-md border border-gray-100 hover:bg-gray-100 hover:scale-110 transition-all cursor-pointer"
            aria-label="Close Custom Plan"
          >
            <X className="h-3 w-3" strokeWidth={3} />
          </button>
        </div>
      </div>

      <TripQuizDialog open={quizOpen} onOpenChange={setQuizOpen} />
    </>
  );
};

export default FloatingTripQuiz;
